/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type FormattedContentStackPost } from '@/features/contentstack/interface/models';
import dayjs from '@/lib/dayjs';

type ArticleSchema = {
  '@context': 'https://schema.org';
  '@type': 'Article';
  headline: string;
  description: string;
  image?: string;
  dateModified?: string;
  author: {
    '@type': 'Organization';
    name: string;
  };
  publisher: {
    '@type': 'Organization';
    name: string;
    logo: {
      '@type': 'ImageObject';
      url: string;
    };
  };
  mainEntityOfPage: {
    '@type': 'WebPage';
    '@id': string;
  };
};

export const getArticleSchema = ({
  blogData,
  currentURL,
  baseURL,
}: {
  blogData: FormattedContentStackPost['blogContent'];
  currentURL: string;
  baseURL: string;
}): ArticleSchema => {
  const fullURL = `${baseURL}${currentURL}`;

  const isImage = blogData.feature_content?.media_type === 'Image';

  const imageUrl = isImage
    ? blogData.feature_content?.image?.image_url
    : blogData.feature_content?.video?.poster_image;

  const dateModified = blogData.updated_at
    ? dayjs(blogData.updated_at).format('YYYY-MM-DD')
    : undefined;

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: blogData.title,
    description: blogData?.seo?.meta_description || '',
    ...(imageUrl && { image: imageUrl }),
    ...(dateModified && { dateModified }),
    author: {
      '@type': 'Organization',
      name: 'AutoZone',
    },
    publisher: {
      '@type': 'Organization',
      name: 'AutoZone',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.autozone.com/images/az-logo-full.svg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': fullURL,
    },
  };
};
